import React, { useState, useMemo } from "react";
import {
  Drawer,
  Box,
  List,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  Collapse,
} from "@mui/material";
import {
  ExpandLess,
  ExpandMore,
  Home,
  CloudUpload,
} from "@mui/icons-material";

import MenuIcon from "@mui/icons-material/Menu";
import IconButton from "@mui/material/IconButton";
import useMediaQuery from "@mui/material/useMediaQuery";
import { useTheme } from "@mui/material/styles";
import DashboardIcon from "@mui/icons-material/Dashboard";
import CallIcon from "@mui/icons-material/Call";
import BusinessIcon from "@mui/icons-material/Business";
import GroupIcon from "@mui/icons-material/Group";

import { useNavigate, useLocation } from "react-router-dom";
import SrifinLogo from "../assets/srifin_final.svg";
import { NAVY, SLATE, GOLD } from "../theme";

// Light steel-blue used for inactive icons — close to the original #93C5FD,
// kept because it reads well against NAVY.
const ICON_BLUE = "#93C5FD";

const MODULES = {
  HOME: "IT-HOME",
  APPROVAL: "IT-APPROVAL",
  NEW_ASSETS: "IT-NEW ASSETS",
  BULK_UPLOAD: "IT-Bulk Upload",
  ASSET_DEPRECIATION: "IT-Asset Depreciation",
  USER_ROLES: "IT-User Roles",
  MANAGER_REPORT: "IT-REPORT",

  HO_REPORT: "HO_Report",
  HO_ASSIGN: "HO_Assign",
  HO_RECORN: "HO_Recorn",

  RO_REPORT: "RO_Report",
  RO_ACCEPT: "RO_Accept",
  RO_ASSIGN: "RO_Assign",

  BO_REPORT: "BO_Report",
  BO_ACCEPT: "BO_Accept",
  BO_ASSIGN: "BO_Assign",

  CALL_REPORT: "CALL_Report",
  CALL_ADMIN: "CALL_ADMIN",
  CALL_Telecaller: "CALL_Telecaller",
  CALL_Branch: "CALL_Branch",
};

const Sidebar = () => {
  const navigate = useNavigate();
  const location = useLocation();

  const [mobileOpen, setMobileOpen] = useState(false);

  const handleDrawerToggle = () => {
    setMobileOpen(!mobileOpen);
  };
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("md"));

  const getSubMenuStyle = (path) => ({
    ...subMenuItemStyles,

    bgcolor: location.pathname === path ? `${SLATE}66` : "transparent",
    color: "#fff",
    "& .MuiListItemIcon-root": {
      color: location.pathname === path ? GOLD : ICON_BLUE,
    },
    "&:hover": {
      bgcolor: location.pathname === path ? `${SLATE}99` : SLATE,
    },
    "&::after":
      location.pathname === path
        ? {
            content: '""',
            width: 8,
            height: 8,
            borderRadius: "50%",
            backgroundColor: GOLD,
            marginLeft: "auto",
          }
        : {},
  });

  const getLevel2Style = (path) => ({
    pl: 2,
    bgcolor: location.pathname === path ? `${SLATE}66` : "transparent",

    color: "#fff",

    "& .MuiListItemIcon-root": {
      color: location.pathname === path ? GOLD : ICON_BLUE,
    },

    "&:hover": {
      bgcolor: location.pathname === path ? `${SLATE}99` : SLATE,
      borderRadius: 1,
    },

    "&::after":
      location.pathname === path
        ? {
            content: '""',
            width: 8,
            height: 8,
            borderRadius: "50%",
            backgroundColor: GOLD,
            marginLeft: "auto",
          }
        : {},
  });

  const [openMenus, setOpenMenus] = useState(() => {
    const saved = localStorage.getItem("sidebarMenus");
    return saved ? JSON.parse(saved) : {};
  });
  const toggleMenu = (menu) => {
    setOpenMenus((prev) => {
      const updated = {
        ...prev,
        [menu]: !prev[menu],
      };

      localStorage.setItem("sidebarMenus", JSON.stringify(updated));

      return updated;
    });
  };

  const allowedModules = JSON.parse(
    localStorage.getItem("allowedModules") || "[]",
  );

  const handleNavigate = (path) => {
    navigate(path);

    if (isMobile) {
      setMobileOpen(false);
    }
  };

  const isModuleAllowed = (module) => allowedModules.includes(module);

  const hasAMSAccess = [
    MODULES.HOME,
    MODULES.APPROVAL,
    MODULES.NEW_ASSETS,
    MODULES.BULK_UPLOAD,
    MODULES.ASSET_DEPRECIATION,
    MODULES.USER_ROLES,
    MODULES.MANAGER_REPORT,
  ].some((module) => isModuleAllowed(module));

  const hasHOAccess = [
    MODULES.HO_REPORT,
    MODULES.HO_ASSIGN,
    MODULES.HO_RECORN,
  ].some((module) => isModuleAllowed(module));

  const hasROAccess = [
    MODULES.RO_REPORT,
    MODULES.RO_ACCEPT,
    MODULES.RO_ASSIGN,
  ].some((module) => isModuleAllowed(module));

  const hasBOAccess = [
    MODULES.BO_REPORT,
    MODULES.BO_ACCEPT,
    MODULES.BO_ASSIGN,
  ].some((module) => isModuleAllowed(module));

  const hasDebitCardAccess = hasHOAccess || hasROAccess || hasBOAccess;

  const hasTrackAccess = [
    MODULES.CALL_REPORT,
    MODULES.CALL_ADMIN,
    MODULES.CALL_Telecaller,
    MODULES.CALL_Branch,
  ].some((module) => isModuleAllowed(module));

  const approvalMenuItems = useMemo(
    () => [
      { text: "Acceptance", path: "/approval/acceptance" },
      { text: "Free Pool", path: "/approval/free-pool" },
      { text: "Assigned", path: "/approval/assigned" },
      { text: "Under Maintenance", path: "/approval/under-maintenance" },
      { text: "Purchase Order", path: "/approval/po" },
      { text: "Invoice", path: "/approval/invoice" },
      { text: "Payment Receipt", path: "/approval/payment" },
      { text: "Bulk Upload", path: "/approval/bulk" },
    ],
    [],
  );

  const newAssetsMenuItems = useMemo(
    () => [
      { text: "Purchase Order", path: "/new-assets/purchesorder" },
      { text: "Create PO", path: "/new-assets/create-po" },
      { text: "Edit PO", path: "/new-assets/edit-po" },
      { text: "Upload Invoice", path: "/new-assets/upload-invoice" },
      { text: "Upload Payment Receipt", path: "/new-assets/upload-reciept" },
    ],
    [],
  );
  return (
    <>
      {isMobile && (
        <IconButton
          onClick={handleDrawerToggle}
          sx={{
            position: "fixed",
            top: 15,
            left: 10,
            zIndex: 3000,
            color: "#fff",
            bgcolor: NAVY,
            "&:hover": {
              bgcolor: SLATE,
            },
          }}
        >
          <MenuIcon />
        </IconButton>
      )}

      <Drawer
        variant={isMobile ? "temporary" : "permanent"}
        open={isMobile ? mobileOpen : true}
        onClose={handleDrawerToggle}
        sx={{
          "& .MuiDrawer-paper": {
            width: 250,
            bgcolor: NAVY,
            color: "#fff",
            overflowY: "auto",
            border: "none",
          },
        }}
      >
        <Box
          sx={{
            p: 2,
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
            borderBottom: `1px solid ${GOLD}33`,
          }}
        >
          <img
            src={SrifinLogo}
            alt="Srifin Logo"
            onClick={() => navigate("/DashBoard")}
            style={{
              width: 160,
              height: 40,
              backgroundColor: "white",
              padding: "5px 0px",
              borderRadius: "8px",
              marginRight: 16,
              cursor: "pointer",
            }}
          />
        </Box>

        <List>
          {/* Common Dashboard */}
          <ListItemButton
            sx={menuItemStyles}
            onClick={() => handleNavigate("/DashBoard")}
          >
            <ListItemIcon>
              <DashboardIcon sx={{ color: ICON_BLUE }} />
            </ListItemIcon>
            <ListItemText primary="Dashboard" />
          </ListItemButton>

          {/* ================= AMS ================= */}
          {hasAMSAccess && (
            <>
              <ListItemButton
                sx={menuItemStyles}
                onClick={() => toggleMenu("ams")}
              >
                <ListItemIcon>
                  <Home sx={{ color: ICON_BLUE }} />
                </ListItemIcon>

                <ListItemText primary="AMS" />

                {openMenus.ams ? <ExpandLess /> : <ExpandMore />}
              </ListItemButton>

              <Collapse in={openMenus.ams} timeout="auto">
                {isModuleAllowed(MODULES.HOME) && (
                  <ListItemButton
                    sx={getSubMenuStyle("/AMSDashBoard")}
                    onClick={() => handleNavigate("/AMSDashBoard")}
                  >
                    <ListItemIcon></ListItemIcon>
                    <ListItemText primary="Home" />
                  </ListItemButton>
                )}
                {isModuleAllowed(MODULES.MANAGER_REPORT) && (
                  <ListItemButton
                    sx={getSubMenuStyle("/manager-report")}
                    onClick={() => handleNavigate("/manager-report")}
                  >
                    <ListItemIcon></ListItemIcon>
                    <ListItemText primary="Assets Report" />
                  </ListItemButton>
                )}

                {/* Approval */}
                {isModuleAllowed(MODULES.APPROVAL) && (
                  <>
                    <ListItemButton
                      sx={subMenuItemStyles}
                      onClick={() => toggleMenu("approval")}
                    >
                      <ListItemIcon></ListItemIcon>

                      <ListItemText primary="Approval" />

                      {openMenus.approval ? <ExpandLess /> : <ExpandMore />}
                    </ListItemButton>

                    <Collapse in={openMenus.approval}>
                      {approvalMenuItems.map((item, index) => (
                        <ListItemButton
                          key={index}
                          sx={getLevel2Style(item.path)}
                          onClick={() => handleNavigate(item.path)}
                        >
                          <ListItemIcon sx={{ color: ICON_BLUE }}>
                            {item.icon}
                          </ListItemIcon>
                          <ListItemText primary={item.text} />
                        </ListItemButton>
                      ))}
                    </Collapse>
                  </>
                )}

                {/* New Assets */}
                {isModuleAllowed(MODULES.NEW_ASSETS) && (
                  <>
                    <ListItemButton
                      sx={subMenuItemStyles}
                      onClick={() => toggleMenu("newAssets")}
                    >
                      <ListItemIcon></ListItemIcon>

                      <ListItemText primary="New Assets" />

                      {openMenus.newAssets ? <ExpandLess /> : <ExpandMore />}
                    </ListItemButton>

                    <Collapse in={openMenus.newAssets}>
                      {newAssetsMenuItems.map((item, index) => (
                        <ListItemButton
                          key={index}
                          sx={getLevel2Style(item.path)}
                          onClick={() => handleNavigate(item.path)}
                        >
                          <ListItemIcon sx={{ color: ICON_BLUE }}>
                            {item.icon}
                          </ListItemIcon>

                          <ListItemText primary={item.text} />
                        </ListItemButton>
                      ))}
                    </Collapse>
                  </>
                )}

                {isModuleAllowed(MODULES.BULK_UPLOAD) && (
                  <ListItemButton
                    sx={getSubMenuStyle("/BulkUpload")}
                    onClick={() => handleNavigate("/BulkUpload")}
                  >
                    <ListItemIcon>
                      <CloudUpload sx={{ color: ICON_BLUE }} />
                    </ListItemIcon>
                    <ListItemText primary="Bulk Upload" />
                  </ListItemButton>
                )}

                {isModuleAllowed(MODULES.USER_ROLES) && (
                  <ListItemButton
                    sx={getSubMenuStyle("/user_roles")}
                    onClick={() => handleNavigate("/user_roles")}
                  >
                    <ListItemIcon>
                      <GroupIcon sx={{ color: ICON_BLUE }} />
                    </ListItemIcon>
                    <ListItemText primary="User Roles" />
                  </ListItemButton>
                )}
              </Collapse>
            </>
          )}

          {/* ================= DEBIT CARD ================= */}
          {hasDebitCardAccess && (
            <>
              <ListItemButton
                sx={menuItemStyles}
                onClick={() => toggleMenu("debit")}
              >
                <ListItemIcon>
                  <BusinessIcon sx={{ color: ICON_BLUE }} />
                </ListItemIcon>

                <ListItemText primary="Debit Card" />

                {openMenus.debit ? <ExpandLess /> : <ExpandMore />}
              </ListItemButton>

              <Collapse in={openMenus.debit}>
                {/* HO */}
                {hasHOAccess && (
                  <>
                    <ListItemButton
                      sx={subMenuItemStyles}
                      onClick={() => toggleMenu("ho")}
                    >
                      <ListItemText primary="HO" />
                      {openMenus.ho ? <ExpandLess /> : <ExpandMore />}
                    </ListItemButton>

                    <Collapse in={openMenus.ho}>
                      {isModuleAllowed(MODULES.HO_REPORT) && (
                        <ListItemButton
                          sx={getSubMenuStyle("/ho-user/horeport")}
                          onClick={() => handleNavigate("/ho-user/horeport")}
                        >
                          <ListItemText primary="Report" />
                        </ListItemButton>
                      )}

                      {isModuleAllowed(MODULES.HO_ASSIGN) && (
                        <ListItemButton
                          sx={getSubMenuStyle("/ho-user/credit_bulk-upload")}
                          onClick={() =>
                            handleNavigate("/ho-user/credit_bulk-upload")
                          }
                        >
                          <ListItemText primary="Assign" />
                        </ListItemButton>
                      )}

                      {isModuleAllowed(MODULES.HO_RECORN) && (
                        <ListItemButton
                          sx={getSubMenuStyle("/recorn")}
                          onClick={() => handleNavigate("/recorn")}
                        >
                          <ListItemText primary="Recorn" />
                        </ListItemButton>
                      )}
                    </Collapse>
                  </>
                )}

                {/* RO */}
                {hasROAccess && (
                  <>
                    <ListItemButton
                      sx={subMenuItemStyles}
                      onClick={() => toggleMenu("ro")}
                    >
                      <ListItemText primary="RO" />
                      {openMenus.ro ? <ExpandLess /> : <ExpandMore />}
                    </ListItemButton>

                    <Collapse in={openMenus.ro}>
                      {isModuleAllowed(MODULES.RO_REPORT) && (
                        <ListItemButton
                          sx={getSubMenuStyle("/ro-user/roreport")}
                          onClick={() => handleNavigate("/ro-user/roreport")}
                        >
                          <ListItemText primary="Report" />
                        </ListItemButton>
                      )}

                      {isModuleAllowed(MODULES.RO_ACCEPT) && (
                        <ListItemButton
                          sx={getSubMenuStyle("/ho-user/ropage")}
                          onClick={() => handleNavigate("/ho-user/ropage")}
                        >
                          <ListItemText primary="Accept" />
                        </ListItemButton>
                      )}

                      {isModuleAllowed(MODULES.RO_ASSIGN) && (
                        <ListItemButton
                          sx={getSubMenuStyle("/ho-user/roassign")}
                          onClick={() => handleNavigate("/ho-user/roassign")}
                        >
                          <ListItemText primary="Assign" />
                        </ListItemButton>
                      )}
                    </Collapse>
                  </>
                )}

                {/* BO */}
                {hasBOAccess && (
                  <>
                    <ListItemButton
                      sx={subMenuItemStyles}
                      onClick={() => toggleMenu("bo")}
                    >
                      <ListItemText primary="BO" />
                      {openMenus.bo ? <ExpandLess /> : <ExpandMore />}
                    </ListItemButton>

                    <Collapse in={openMenus.bo}>
                      {isModuleAllowed(MODULES.BO_REPORT) && (
                        <ListItemButton
                          sx={getSubMenuStyle("/bo-user/boreport")}
                          onClick={() => handleNavigate("/bo-user/boreport")}
                        >
                          <ListItemText primary="Report" />
                        </ListItemButton>
                      )}

                      {isModuleAllowed(MODULES.BO_ACCEPT) && (
                        <ListItemButton
                          sx={getSubMenuStyle("/bo-user/bopage")}
                          onClick={() => handleNavigate("/bo-user/bopage")}
                        >
                          <ListItemText primary="Accept" />
                        </ListItemButton>
                      )}

                      {isModuleAllowed(MODULES.BO_ASSIGN) && (
                        <ListItemButton
                          sx={getSubMenuStyle("/bo-user/boassign")}
                          onClick={() => handleNavigate("/bo-user/boassign")}
                        >
                          <ListItemText primary="Assign" />
                        </ListItemButton>
                      )}
                    </Collapse>
                  </>
                )}
              </Collapse>
            </>
          )}

          {/* ================= TRACK ================= */}
          {hasTrackAccess && (
            <>
              <ListItemButton
                sx={menuItemStyles}
                onClick={() => toggleMenu("track")}
              >
                <ListItemIcon>
                  <CallIcon sx={{ color: ICON_BLUE }} />
                </ListItemIcon>

                <ListItemText primary="Call Track" />

                {openMenus.track ? <ExpandLess /> : <ExpandMore />}
              </ListItemButton>

              <Collapse in={openMenus.track}>
                {(isModuleAllowed(MODULES.CALL_ADMIN) ||
                  isModuleAllowed(MODULES.CALL_Telecaller) ||
                  isModuleAllowed(MODULES.CALL_Branch)) && (
                  <ListItemButton
                    sx={getSubMenuStyle("/track/Dashboard")}
                    onClick={() => handleNavigate("/track/Dashboard")}
                  >
                    <ListItemIcon></ListItemIcon>
                    <ListItemText primary="Home" />
                  </ListItemButton>
                )}
                {isModuleAllowed(MODULES.CALL_ADMIN) && (
                  <ListItemButton
                    sx={getSubMenuStyle("/track/Call-BulkUpload")}
                    onClick={() => handleNavigate("/track/Call-BulkUpload")}
                  >
                    <ListItemIcon></ListItemIcon>
                    <ListItemText primary="Upload Sheet" />
                  </ListItemButton>
                )}
                {isModuleAllowed(MODULES.CALL_ADMIN) && (
                  <ListItemButton
                    sx={getSubMenuStyle("/track/sheetList")}
                    onClick={() => handleNavigate("/track/sheetList")}
                  >
                    <ListItemIcon></ListItemIcon>
                    <ListItemText primary="Sheet List" />
                  </ListItemButton>
                )}
                {isModuleAllowed(MODULES.CALL_ADMIN) && (
                  <ListItemButton
                    sx={getSubMenuStyle("/track/questionmanage")}
                    onClick={() => handleNavigate("/track/questionmanage")}
                  >
                    <ListItemIcon></ListItemIcon>
                    <ListItemText primary="Questions Management" />
                  </ListItemButton>
                )}
                {isModuleAllowed(MODULES.CALL_ADMIN) && (
                  <ListItemButton
                    sx={getSubMenuStyle("/track/Call-Report")}
                    onClick={() => handleNavigate("/track/Call-Report")}
                  >
                    <ListItemIcon></ListItemIcon>
                    <ListItemText primary="Call Report" />
                  </ListItemButton>
                )}
                {isModuleAllowed(MODULES.CALL_ADMIN) && (
                  <ListItemButton
                    sx={getSubMenuStyle("/track/Call-search")}
                    onClick={() => handleNavigate("/track/Call-search")}
                  >
                    <ListItemIcon></ListItemIcon>
                    <ListItemText primary="Call Search" />
                  </ListItemButton>
                )}
                {isModuleAllowed(MODULES.CALL_Telecaller) && (
                  <ListItemButton
                    sx={getSubMenuStyle("/tele/Call")}
                    onClick={() => handleNavigate("/tele/Call")}
                  >
                    <ListItemIcon></ListItemIcon>
                    <ListItemText primary="T Customer List" />
                  </ListItemButton>
                )}
                {isModuleAllowed(MODULES.CALL_Branch) && (
                  <ListItemButton
                    sx={getSubMenuStyle("/brch/Call")}
                    onClick={() => handleNavigate("/brch/Call")}
                  >
                    <ListItemIcon></ListItemIcon>
                    <ListItemText primary="B Customer List" />
                  </ListItemButton>
                )}
              </Collapse>
            </>
          )}
        </List>
      </Drawer>
    </>
  );
};

const menuItemStyles = {
  "&:hover": {
    bgcolor: SLATE,
    borderRadius: 1,
  },
};
const subMenuItemStyles = {
  pl: 0,
  "&:hover": {
    bgcolor: SLATE,
    borderRadius: 1,
  },
};

export default Sidebar;